// Admin Settings → Integrations → Etsy listing push (plan
// docs/plans/2026-10-26-etsy-listing-push-v2-plan.md P2b; mockup approved by
// Volkan 2026-10-26). Three tabs:
//   Defaults      — admin_config `etsy_listing.defaults`, global + per-org.
//                   The numbers come from the server (built-in → global → org);
//                   this screen never hard-codes a default.
//   Stores        — the per-store "Etsy listing push" switch.
//   Pushes        — every send with its step results (P5, EtsyPushesTab.tsx);
//                   the Stores tab also carries the go-live checklist.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiUrl } from '../../../lib/api'
import { formatDateTime } from '../../../lib/datetime'
import { toast } from '../../../lib/toast'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { Input } from '../../../components/ui/input'
import { Select } from '../../../components/ui/select'
import { SectionTabs } from '../../../components/ui/route-tabs'
import { OrgSearchModal } from '../../../components/OrgSearchModal'
import type { DirectoryOrg } from '../../../mocks/data/organizations'
import { useT } from '../../../i18n'
import {
  etsyListingSettingsI18n,
  type EtsyListingSettingsStrings,
} from '../../../i18n/admin/etsy-listing-settings'
import {
  clone,
  diffDefaults,
  type EtsyListingDefaults,
  type Section,
} from './etsy-listing-defaults.lib'
import { EtsyPushesTab } from './EtsyPushesTab'
import { EtsyGoLiveCard, SwitchOnDialog } from './EtsyGoLiveCard'
import { GO_LIVE_KEY, useGoLive } from './etsy-pushes.lib'
import { etsyPushAdminI18n } from '../../../i18n/admin/etsy-push-admin'

const CONFIG_KEY = 'etsy_listing.defaults'


type DefaultsRead = {
  built_in: EtsyListingDefaults
  global_value: Partial<Record<Section, Record<string, unknown>>> | null
  org_value: Partial<Record<Section, Record<string, unknown>>> | null
  resolved: EtsyListingDefaults
  updated_at: string | null
  updated_by_admin_id: string | null
}

type OrgOverride = { org_id: string; org_name: string }

type StoreRow = {
  store_integration_id: string
  shop_name: string
  org_id: string
  org_name: string
  active: boolean
  etsy_listing_write_enabled: boolean
  permission_status: 'ok' | 'reconnect_required' | 'disconnected'
  last_push_at: string | null
  push_count_30d: number
  sent_count_30d: number
  is_test_store: boolean
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path))
  if (!res.ok) throw new Error(`${path} ${res.status}`)
  return (await res.json()) as T
}

async function sendJson(path: string, method: 'PUT' | 'PATCH' | 'DELETE', body?: unknown) {
  const res = await fetch(apiUrl(path), {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok && res.status !== 204) {
    let message = `${res.status}`
    try {
      const data = (await res.json()) as { detail?: { message?: string } }
      message = data.detail?.message ?? message
    } catch {
      // keep the status code
    }
    throw new Error(message)
  }
}


export function EtsyListingPushSettings() {
  const t = useT(etsyListingSettingsI18n)
  const [tab, setTab] = useState<'defaults' | 'stores' | 'pushes'>('defaults')
  const [focusPush, setFocusPush] = useState<string | null>(null)
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.title}</h2>
        <p className="mt-1 max-w-[70ch] text-sm text-[var(--color-text-muted)]">{t.intro}</p>
      </div>
      <SectionTabs
        title={t.title}
        hideTitle
        tabs={[
          { id: 'defaults', label: t.tabs.defaults },
          { id: 'stores', label: t.tabs.stores },
          { id: 'pushes', label: t.tabs.pushes },
        ]}
        activeId={tab}
        onSelect={(id) => setTab(id as typeof tab)}
      />
      {tab === 'defaults' ? <DefaultsTab t={t} /> : null}
      {tab === 'stores' ? (
        <StoresTab
          t={t}
          onShowSend={(id) => {
            setFocusPush(id)
            setTab('pushes')
          }}
        />
      ) : null}
      {tab === 'pushes' ? <EtsyPushesTab key={focusPush ?? 'list'} initialSelected={focusPush} /> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

function DefaultsTab({ t }: { t: EtsyListingSettingsStrings }) {
  const qc = useQueryClient()
  const [orgId, setOrgId] = useState<string | null>(null)
  const [picked, setPicked] = useState<OrgOverride | null>(null)
  const [picking, setPicking] = useState(false)

  const overrides = useQuery({
    queryKey: ['admin', 'etsy-listing', 'overrides'],
    queryFn: async () =>
      (await getJson<{ items: OrgOverride[] }>(`/admin/config/${CONFIG_KEY}/orgs`)).items,
  })
  const read = useQuery({
    queryKey: ['admin', 'etsy-listing', 'defaults', orgId],
    queryFn: () =>
      getJson<DefaultsRead>(
        `/admin/etsy-listing/defaults${orgId ? `?org_id=${encodeURIComponent(orgId)}` : ''}`,
      ),
  })
  const globalRead = useQuery({
    queryKey: ['admin', 'etsy-listing', 'defaults', null],
    queryFn: () => getJson<DefaultsRead>('/admin/etsy-listing/defaults'),
    enabled: orgId !== null,
  })

  // The edit draft belongs to the read it started from: a refetch (save,
  // scope change) drops it and the form shows the server values again.
  const [draft, setDraft] = useState<{ source: DefaultsRead; value: EtsyListingDefaults } | null>(
    null,
  )
  const form = read.data
    ? draft && draft.source === read.data
      ? draft.value
      : read.data.resolved
    : null

  const orgOptions = useMemo(() => {
    const rows = [...(overrides.data ?? [])]
    if (picked && !rows.some((r) => r.org_id === picked.org_id)) rows.push(picked)
    return rows.sort((a, b) => a.org_name.localeCompare(b.org_name))
  }, [overrides.data, picked])

  const isCustom = orgId !== null && Boolean(read.data?.org_value)

  const save = useMutation({
    mutationFn: async () => {
      if (!form || !read.data) return
      if (orgId === null) {
        await sendJson(`/admin/config/${CONFIG_KEY}`, 'PUT', {
          config_value: form,
          value_type: 'object',
        })
      } else {
        const base = (globalRead.data ?? read.data).resolved
        await sendJson(`/admin/config/${CONFIG_KEY}/orgs/${orgId}`, 'PUT', {
          config_value: diffDefaults(base, form),
          value_type: 'object',
        })
      }
    },
    onSuccess: () => {
      toast.success(t.saved, { description: t.futureOnly })
      qc.invalidateQueries({ queryKey: ['admin', 'etsy-listing'] })
    },
    onError: (err: Error) => toast.error(t.saveFailed, { description: err.message }),
  })

  const reset = useMutation({
    mutationFn: () => sendJson(`/admin/config/${CONFIG_KEY}/orgs/${orgId}`, 'DELETE'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'etsy-listing'] })
      setPicked(null)
      setOrgId(null)
    },
    onError: (err: Error) => toast.error(t.saveFailed, { description: err.message }),
  })

  function onPick(org: DirectoryOrg) {
    setPicking(false)
    setPicked({ org_id: org.orgId, org_name: org.orgName })
    setOrgId(org.orgId)
  }

  const set = <S extends Section>(section: S, key: keyof EtsyListingDefaults[S], value: unknown) => {
    const source = read.data
    if (!source) return
    setDraft((d) => {
      const base = d && d.source === source ? d.value : clone(source.resolved)
      return { source, value: { ...base, [section]: { ...base[section], [key]: value } } }
    })
  }

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="etsy-defaults-scope" className="text-xs text-[var(--color-text-muted)]">
            {t.scope.label}
          </label>
          <Select
            id="etsy-defaults-scope"
            value={orgId ?? ''}
            onChange={(e) => {
              if (e.target.value === '__add__') {
                setPicking(true)
                return
              }
              setOrgId(e.target.value || null)
            }}
          >
            <option value="">{t.scope.global}</option>
            {orgOptions.map((o) => (
              <option key={o.org_id} value={o.org_id}>
                {o.org_name}
              </option>
            ))}
            <option value="__add__">{t.scope.addOrganization}</option>
          </Select>
          {isCustom ? <Badge variant="warning">{t.scope.custom}</Badge> : null}
        </div>
        {isCustom ? (
          <Button variant="link" size="sm" onClick={() => reset.mutate()} disabled={reset.isPending}>
            {t.scope.resetToGlobal}
          </Button>
        ) : null}
      </Card>

      {read.isError ? (
        <Card className="p-4 text-sm text-[var(--color-danger)]" role="status">
          {t.loadError}
        </Card>
      ) : !form ? null : (
        <DefaultsForm
          t={t}
          form={form}
          set={set}
          updatedAt={read.data?.updated_at ?? null}
          saving={save.isPending}
          onCancel={() => setDraft(null)}
          onSave={() => save.mutate()}
        />
      )}

      <OrgSearchModal
        side={picking ? 'seller' : null}
        open={picking}
        onClose={() => setPicking(false)}
        onSelect={onPick}
        actionLabel={t.scope.pickerAction}
        description={t.scope.pickerDescription}
      />
    </div>
  )
}

function FieldRow({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 text-sm">
      <label htmlFor={id} className="font-medium text-[var(--color-text)]">
        {label}
      </label>
      {children}
    </div>
  )
}

function Money({
  id,
  value,
  onChange,
  label,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  label?: string
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span aria-hidden="true" className="text-[var(--color-text-muted)]">
        $
      </span>
      <Input
        id={id}
        aria-label={label}
        type="number"
        inputMode="decimal"
        min={0}
        step="0.01"
        className="w-28 tabular-nums"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

function NumberInput({
  id,
  value,
  onChange,
  min = 0,
  max,
  label,
  disabled,
}: {
  id: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  label?: string
  disabled?: boolean
}) {
  return (
    <Input
      id={id}
      aria-label={label}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      disabled={disabled}
      className="w-24 tabular-nums"
      value={Number.isFinite(value) ? String(value) : ''}
      onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
    />
  )
}

function Radio({
  name,
  value,
  current,
  label,
  onChange,
  children,
}: {
  name: string
  value: string
  current: string
  label: string
  onChange: (v: string) => void
  children?: React.ReactNode
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-sm text-[var(--color-text)]">
      <input
        type="radio"
        name={name}
        value={value}
        checked={current === value}
        onChange={() => onChange(value)}
      />
      {label}
      {children}
    </label>
  )
}

function SectionCard({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Card className="space-y-4 p-5">
      <div>
        <h3 className="text-[15px] font-semibold text-[var(--color-text)]">{title}</h3>
        {hint ? <p className="text-xs text-[var(--color-text-muted)]">{hint}</p> : null}
      </div>
      {children}
    </Card>
  )
}

function DefaultsForm({
  t,
  form,
  set,
  updatedAt,
  saving,
  onCancel,
  onSave,
}: {
  t: EtsyListingSettingsStrings
  form: EtsyListingDefaults
  set: <S extends Section>(section: S, key: keyof EtsyListingDefaults[S], value: unknown) => void
  updatedAt: string | null
  saving: boolean
  onCancel: () => void
  onSave: () => void
}) {
  const s = form.shipping
  const p = form.processing
  const q = form.quantity
  const d = form.declaration
  const grid = 'grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2'
  return (
    // noValidate: the server is the one validator (admin config PUT → 422 with
    // the field and reason); the browser's step arithmetic must not block a
    // valid amount.
    <form
      noValidate
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        onSave()
      }}
    >
      <SectionCard title={t.shipping.title} hint={t.shipping.hint}>
        <div className={grid}>
          <FieldRow id="etsy-sp-title" label={t.shipping.profileName}>
            <Input id="etsy-sp-title" value={s.title} onChange={(e) => set('shipping', 'title', e.target.value)} />
          </FieldRow>
          <FieldRow id="etsy-sp-zip" label={t.shipping.originZip}>
            <Input
              id="etsy-sp-zip"
              value={s.origin_postal_code}
              maxLength={20}
              onChange={(e) => set('shipping', 'origin_postal_code', e.target.value)}
            />
          </FieldRow>
          <FieldRow id="etsy-sp-first" label={t.shipping.usFirst}>
            <Money id="etsy-sp-first" value={s.us_first_usd} onChange={(v) => set('shipping', 'us_first_usd', v)} />
          </FieldRow>
          <FieldRow id="etsy-sp-add" label={t.shipping.usAdditional}>
            <Money id="etsy-sp-add" value={s.us_additional_usd} onChange={(v) => set('shipping', 'us_additional_usd', v)} />
          </FieldRow>
          <FieldRow id="etsy-sp-dmin" label={t.shipping.deliveryDays}>
            <div className="flex items-center gap-2">
              <NumberInput id="etsy-sp-dmin" min={1} max={90} value={s.delivery_min_days} onChange={(v) => set('shipping', 'delivery_min_days', v)} />
              <span aria-hidden="true">–</span>
              <NumberInput
                id="etsy-sp-dmax"
                label={t.shipping.deliveryMax}
                min={1}
                max={90}
                value={s.delivery_max_days}
                onChange={(v) => set('shipping', 'delivery_max_days', v)}
              />
            </div>
          </FieldRow>
          <div className="grid gap-1 text-sm">
            <span className="font-medium text-[var(--color-text)]">{t.shipping.international}</span>
            <div role="radiogroup" aria-label={t.shipping.international} className="flex flex-wrap gap-2">
              <Radio name="etsy-intl" value="off" current={s.international} label={t.shipping.intlOff} onChange={(v) => set('shipping', 'international', v)} />
              <Radio name="etsy-intl" value="flat" current={s.international} label={t.shipping.intlFlat} onChange={(v) => set('shipping', 'international', v)} />
            </div>
          </div>
          {s.international === 'flat' ? (
            <>
              <FieldRow id="etsy-sp-ifirst" label={t.shipping.intlFirst}>
                <Money id="etsy-sp-ifirst" value={s.intl_first_usd} onChange={(v) => set('shipping', 'intl_first_usd', v)} />
              </FieldRow>
              <FieldRow id="etsy-sp-iadd" label={t.shipping.intlAdditional}>
                <Money id="etsy-sp-iadd" value={s.intl_additional_usd} onChange={(v) => set('shipping', 'intl_additional_usd', v)} />
              </FieldRow>
            </>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard title={t.processing.title} hint={t.processing.hint}>
        <div role="radiogroup" aria-label={t.processing.title} className="flex flex-wrap gap-2">
          <Radio name="etsy-proc" value="catalog" current={p.source} label={t.processing.fromCatalog} onChange={(v) => set('processing', 'source', v)} />
          <Radio name="etsy-proc" value="manual" current={p.source} label={t.processing.manual} onChange={(v) => set('processing', 'source', v)} />
        </div>
        <div className={grid}>
          <FieldRow id="etsy-pr-unit" label={t.processing.unit}>
            <Select id="etsy-pr-unit" value={p.unit} onChange={(e) => set('processing', 'unit', e.target.value)}>
              <option value="days">{t.processing.days}</option>
              <option value="weeks">{t.processing.weeks}</option>
            </Select>
          </FieldRow>
          <FieldRow id="etsy-pr-min" label={t.processing.manualRange}>
            <div className="flex items-center gap-2">
              <NumberInput id="etsy-pr-min" min={1} max={90} disabled={p.source !== 'manual'} value={p.manual_min} onChange={(v) => set('processing', 'manual_min', v)} />
              <span aria-hidden="true">–</span>
              <NumberInput
                id="etsy-pr-max"
                label={t.processing.manualMax}
                min={1}
                max={90}
                disabled={p.source !== 'manual'}
                value={p.manual_max}
                onChange={(v) => set('processing', 'manual_max', v)}
              />
            </div>
          </FieldRow>
        </div>
        <label className="flex items-start gap-2 text-sm text-[var(--color-text)]">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={p.ready_to_ship_when_stocked}
            onChange={(e) => set('processing', 'ready_to_ship_when_stocked', e.target.checked)}
          />
          <span>{t.processing.readyToShip}</span>
        </label>
      </SectionCard>

      <SectionCard title={t.quantity.title} hint={t.quantity.hint}>
        <div className={grid}>
          <div className="grid gap-1 text-sm">
            <span className="font-medium text-[var(--color-text)]">{t.quantity.stocked}</span>
            <div role="radiogroup" aria-label={t.quantity.stocked} className="flex flex-wrap gap-2">
              <Radio name="etsy-q1" value="stock" current={q.stocked} label={t.quantity.stockCount} onChange={(v) => set('quantity', 'stocked', v)} />
              <Radio name="etsy-q1" value="fixed" current={q.stocked} label={t.quantity.fixed} onChange={(v) => set('quantity', 'stocked', v)}>
                <NumberInput id="etsy-q1-fixed" label={t.quantity.fixedAmount} max={999} value={q.stocked_fixed} onChange={(v) => set('quantity', 'stocked_fixed', v)} />
              </Radio>
            </div>
          </div>
          <div className="grid gap-1 text-sm">
            <span className="font-medium text-[var(--color-text)]">{t.quantity.madeToOrder}</span>
            <div role="radiogroup" aria-label={t.quantity.madeToOrder} className="flex flex-wrap gap-2">
              <Radio name="etsy-q2" value="max" current={q.made_to_order} label="999" onChange={(v) => set('quantity', 'made_to_order', v)} />
              <Radio name="etsy-q2" value="fixed" current={q.made_to_order} label={t.quantity.fixed} onChange={(v) => set('quantity', 'made_to_order', v)}>
                <NumberInput id="etsy-q2-fixed" label={t.quantity.fixedAmount} max={999} value={q.made_to_order_fixed} onChange={(v) => set('quantity', 'made_to_order_fixed', v)} />
              </Radio>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t.declaration.title}>
        <div className={grid}>
          <FieldRow id="etsy-who" label={t.declaration.whoMade}>
            <Select id="etsy-who" value={d.who_made} onChange={(e) => set('declaration', 'who_made', e.target.value)}>
              {(Object.keys(t.declaration.whoMadeOptions) as Array<keyof typeof t.declaration.whoMadeOptions>).map((k) => (
                <option key={k} value={k}>
                  {t.declaration.whoMadeOptions[k]}
                </option>
              ))}
            </Select>
          </FieldRow>
          <FieldRow id="etsy-when" label={t.declaration.whenMade}>
            <Select id="etsy-when" value={d.when_made} onChange={(e) => set('declaration', 'when_made', e.target.value)}>
              {Object.entries(t.declaration.whenMadeOptions).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
              {d.when_made in t.declaration.whenMadeOptions ? null : (
                <option value={d.when_made}>{d.when_made}</option>
              )}
            </Select>
          </FieldRow>
          <FieldRow id="etsy-mult" label={t.declaration.fallbackPrice}>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              {t.declaration.fallbackPriceSuffix}
              <Input
                id="etsy-mult"
                type="number"
                inputMode="decimal"
                min={1.01}
                step="0.01"
                className="w-24 tabular-nums"
                value={form.pricing.fallback_multiplier}
                onChange={(e) => set('pricing', 'fallback_multiplier', e.target.value)}
              />
            </div>
          </FieldRow>
          <FieldRow id="etsy-bulk" label={t.declaration.bulkMargin}>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              %
              <NumberInput id="etsy-bulk" max={1000} value={form.pricing.bulk_margin_percent} onChange={(v) => set('pricing', 'bulk_margin_percent', v)} />
            </div>
          </FieldRow>
        </div>
      </SectionCard>

      <SectionCard title={t.checklist.title} hint={t.checklist.hint}>
        <div className="space-y-2">
          {(Object.keys(form.checklist) as Array<keyof EtsyListingDefaults['checklist']>).map((key) => (
            <label key={key} className="flex items-start gap-2 text-sm text-[var(--color-text)]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.checklist[key]}
                onChange={(e) => set('checklist', key, e.target.checked)}
              />
              <span>{t.checklist[key]}</span>
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] pt-4">
          <span className="text-xs text-[var(--color-text-muted)]">
            {updatedAt ? t.lastChanged(formatDateTime(updatedAt)) : t.futureOnly}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              {t.cancel}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t.saving : t.save}
            </Button>
          </div>
        </div>
      </SectionCard>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------

function PermissionBadge({ t, status }: { t: EtsyListingSettingsStrings; status: StoreRow['permission_status'] }) {
  if (status === 'ok') return <Badge variant="success">{t.stores.ok}</Badge>
  if (status === 'reconnect_required') return <Badge variant="warning">{t.stores.reconnect}</Badge>
  return <Badge variant="neutral">{t.stores.disconnected}</Badge>
}

function StoresTab({ t, onShowSend }: { t: EtsyListingSettingsStrings; onShowSend: (jobId: string) => void }) {
  const ta = useT(etsyPushAdminI18n).stores
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [turningOn, setTurningOn] = useState<StoreRow | null>(null)
  const goLive = useGoLive()
  const stores = useQuery({
    queryKey: ['admin', 'etsy-listing', 'stores'],
    queryFn: async () => (await getJson<{ items: StoreRow[] }>('/admin/etsy-listing/stores')).items,
  })
  const flip = useMutation({
    mutationFn: async (row: StoreRow) => {
      await sendJson(`/admin/etsy-listing/stores/${row.store_integration_id}`, 'PATCH', {
        enabled: !row.etsy_listing_write_enabled,
      })
      return row
    },
    onSuccess: (row) => {
      toast.success(
        row.etsy_listing_write_enabled ? t.stores.turnedOff(row.shop_name) : t.stores.turnedOn(row.shop_name),
      )
      setTurningOn(null)
      qc.invalidateQueries({ queryKey: ['admin', 'etsy-listing', 'stores'] })
      qc.invalidateQueries({ queryKey: GO_LIVE_KEY })
    },
    onError: (err: Error) => toast.error(t.stores.switchFailed, { description: err.message }),
  })

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const rows = (stores.data ?? []).filter(
      (r) => !needle || r.shop_name.toLowerCase().includes(needle) || r.org_name.toLowerCase().includes(needle),
    )
    const by = new Map<string, StoreRow[]>()
    for (const r of rows) by.set(r.org_name, [...(by.get(r.org_name) ?? []), r])
    return [...by.entries()]
  }, [stores.data, q])

  return (
    <div className="grid items-start gap-4 min-[1100px]:grid-cols-[minmax(0,1fr)_360px]">
    <Card className="min-w-0 space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-[var(--color-text)]">{t.stores.title}</h3>
          <p className="text-xs text-[var(--color-text-muted)]">{t.stores.hint}</p>
        </div>
        <Input
          id="etsy-store-search"
          aria-label={t.stores.search}
          placeholder={t.stores.search}
          className="w-56"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {stores.isError ? (
        <p className="text-sm text-[var(--color-danger)]" role="status">
          {t.loadError}
        </p>
      ) : stores.isLoading ? null : groups.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.stores.empty}</p>
      ) : (
        <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.stores.store}</th>
                <th>{t.stores.permissions}</th>
                <th>{ta.count30d}</th>
                <th>{t.stores.lastPush}</th>
                <th>{t.stores.push}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([org, rows]) => [
                <tr key={`g-${org}`} className="!h-8">
                  <td colSpan={5} className="bg-[var(--color-surface-muted)] py-1.5 text-xs font-semibold text-[var(--color-text-muted)]">
                    {org}
                  </td>
                </tr>,
                ...rows.map((row) => (
                  <tr key={row.store_integration_id}>
                    <td className="text-[var(--color-text)]">
                      {row.shop_name}
                      {row.is_test_store ? (
                        <span className="ml-1.5 rounded-md bg-[color-mix(in_srgb,var(--color-info)_14%,transparent)] px-1.5 py-px text-[11.5px] font-semibold text-[var(--color-info)]">
                          {ta.testTag}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <PermissionBadge t={t} status={row.permission_status} />
                    </td>
                    <td className="whitespace-nowrap tabular-nums text-[var(--color-text-muted)]">
                      {row.push_count_30d ? ta.counts(row.push_count_30d, row.sent_count_30d) : '—'}
                    </td>
                    <td className="whitespace-nowrap tabular-nums text-[var(--color-text-muted)]">
                      {row.last_push_at ? formatDateTime(row.last_push_at) : '—'}
                    </td>
                    <td>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label={t.stores.switchLabel(row.shop_name)}
                          checked={row.etsy_listing_write_enabled}
                          disabled={
                            flip.isPending ||
                            (!row.etsy_listing_write_enabled && row.permission_status !== 'ok')
                          }
                          title={
                            !row.etsy_listing_write_enabled && row.permission_status !== 'ok'
                              ? ta.blockedHint
                              : undefined
                          }
                          onChange={() =>
                            row.etsy_listing_write_enabled ? flip.mutate(row) : setTurningOn(row)
                          }
                          className="h-5 w-5 cursor-pointer"
                        />
                      </label>
                    </td>
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </DataTableShell>
      )}
      <p className="text-xs text-[var(--color-text-muted)]">{ta.blockedHint}</p>
      <SwitchOnDialog
        shop={turningOn?.shop_name ?? ''}
        open={turningOn !== null}
        checked={!!goLive.data?.confirmed_at}
        pending={flip.isPending}
        onCancel={() => setTurningOn(null)}
        onConfirm={() => turningOn && flip.mutate(turningOn)}
      />
    </Card>
    <EtsyGoLiveCard onShowSend={onShowSend} />
    </div>
  )
}
